import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MemberAvatar } from '@/components/MemberAvatar';
import { Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export function LeadDetailShareDialog({
  open, onClose, leadName, members, groups,
  selectedMembers, setSelectedMembers,
  selectedGroups, setSelectedGroups,
  shareTab, setShareTab, onShare,
  shareNote, setShareNote,
}) {
  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleGroup = (id) => {
    setSelectedGroups(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-sm" data-testid="lead-detail-share-dialog">
        <DialogHeader><DialogTitle className="font-heading">Share Lead</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 mb-3">Share "{leadName}" with members or groups:</p>
        <div className="flex gap-1 mb-3 border-b border-slate-200">
          <button onClick={() => setShareTab('members')} className={`px-3 py-1.5 text-xs font-medium transition-colors ${shareTab === 'members' ? 'text-[#0047AB] border-b-2 border-[#0047AB]' : 'text-slate-500 hover:text-slate-700'}`} data-testid="detail-share-tab-members">Members</button>
          <button onClick={() => setShareTab('groups')} className={`px-3 py-1.5 text-xs font-medium transition-colors ${shareTab === 'groups' ? 'text-[#0047AB] border-b-2 border-[#0047AB]' : 'text-slate-500 hover:text-slate-700'}`} data-testid="detail-share-tab-groups">Groups</button>
        </div>
        {shareTab === 'members' ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {members.map(m => (
              <label key={m.id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer" data-testid={`detail-share-member-${m.id}`}>
                <input type="checkbox" checked={selectedMembers.includes(m.id)} onChange={() => toggleMember(m.id)} className="rounded-sm" />
                <MemberAvatar userId={m.id} name={m.name} size="sm" />
                <div><p className="text-sm font-medium text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></div>
              </label>
            ))}
            {members.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No members available</p>}
          </div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {groups.map(g => (
              <label key={g.id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer" data-testid={`detail-share-group-${g.id}`}>
                <input type="checkbox" checked={selectedGroups.includes(g.id)} onChange={() => toggleGroup(g.id)} className="rounded-sm" />
                <div className="w-7 h-7 bg-[#EFF6FF] border border-[#0047AB]/20 rounded-sm flex items-center justify-center shrink-0"><Users className="w-3.5 h-3.5 text-[#0047AB]" /></div>
                <div><p className="text-sm font-medium text-slate-900">{g.name}</p><p className="text-xs text-slate-500">{g.member_names?.length || 0} members</p></div>
              </label>
            ))}
            {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No groups available. <Link to="/groups" className="text-[#0047AB] hover:underline">Create one</Link></p>}
          </div>
        )}
        {/* Share Note */}
        <div>
          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Add a note (optional)</label>
          <textarea
            value={shareNote || ''}
            onChange={e => setShareNote(e.target.value)}
            placeholder="e.g. Hot lead — call ASAP..."
            className="mt-1 w-full border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#0047AB] resize-none"
            rows={2}
            data-testid="detail-share-note-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onShare} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" disabled={selectedMembers.length === 0 && selectedGroups.length === 0} data-testid="detail-share-submit-btn">Share</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
